import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Modal, Icon } from "../../";
import { IntegrationCard } from "../../Business";
import { POSBusinessCard } from "../";
import SalesChannelBusinessCard from "../../SalesChannels/SalesChannelBusinessCard";
import SalesChannelIntegrationCard from "../../SalesChannels/SalesChannelIntegrationCard";
import { Pagination } from "../../Data";
import { useBusinessesForIntegrations } from "../../../../hooks";
import { useThirdPartyIntegrations } from "../../../../hooks";

const BusinessSelectionModal = ({
  isOpen,
  onClose,
  integration,
  integrationType = "pos",
}) => {
  const queryClient = useQueryClient();
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [enabledFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const { data, isLoading, error, refetch } = useBusinessesForIntegrations({
    enabledFilter,
    searchTerm,
    page: currentPage,
    pageSize,
  });

  const { data: integrationsData } = useThirdPartyIntegrations();

  const allIntegrations = React.useMemo(() => {
    if (!integrationsData?.integrations) return [];
    return integrationsData.integrations.filter(
      (integ) => integ.type === integrationType,
    );
  }, [integrationsData?.integrations, integrationType]);

  const businesses = data?.businesses || [];
  const pagination = data?.pagination || {};

  useEffect(() => {
    if (!isOpen) {
      setSelectedBusiness(null);
      setSearchInput("");
      setSearchTerm("");
      setCurrentPage(1);
    }
  }, [isOpen]);

  const handleBusinessSelect = (business) => {
    setSelectedBusiness(business);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setSelectedBusiness(null);
    refetch();
    queryClient.invalidateQueries({
      queryKey: ["thirdPartyIntegrations"],
      exact: false,
    });
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  const handleRefresh = () => {
    refetch();
  };

  useEffect(() => {
    if (!selectedBusiness || businesses.length === 0) return;

    const updatedBusiness = businesses.find(
      (b) => b.id === selectedBusiness.id,
    );
    if (!updatedBusiness) return;

    const currentIntegrations = JSON.stringify(
      selectedBusiness.third_party_integrations || [],
    );
    const updatedIntegrations = JSON.stringify(
      updatedBusiness.third_party_integrations || [],
    );
    if (currentIntegrations !== updatedIntegrations) {
      setSelectedBusiness(updatedBusiness);
    }
  }, [businesses]);

  if (selectedBusiness) {
    const selectedIntegration = allIntegrations.find((int) =>
      selectedBusiness.third_party_integrations?.find(
        (i) => i?.third_party_integration?.id === int.id,
      ),
    );

    const IntegrationCardComponent =
      integrationType === "sales_channel"
        ? SalesChannelIntegrationCard
        : IntegrationCard;

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`${integration?.name} - ${selectedBusiness.name}`}
        size="lg"
      >
        <div className="space-y-4">
          <button
            onClick={handleBack}
            className="mb-4 flex items-center text-sm text-gray-400 transition-colors hover:text-white"
          >
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to store selection
          </button>

          <IntegrationCardComponent
            integration={
              allIntegrations.find((i) => i.id === integration?.id) ||
              integration
            }
            {...(integrationType === "pos" && {
              currentIntegration: selectedIntegration,
            })}
            business={selectedBusiness}
            onBusinessUpdate={() => {
              refetch();
              queryClient.invalidateQueries({
                queryKey: ["thirdPartyIntegrations"],
                exact: false,
              });
            }}
            onIntegrationsUpdate={() => {
              refetch();
              queryClient.invalidateQueries({
                queryKey: ["thirdPartyIntegrations"],
                exact: false,
              });
            }}
          />
        </div>
      </Modal>
    );
  }

  // Determine which BusinessCard component to use
  const BusinessCard =
    integrationType === "sales_channel"
      ? SalesChannelBusinessCard
      : POSBusinessCard;
  const integrationLabel =
    integrationType === "sales_channel" ? "Sales Channel" : "POS";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Select Store - ${integration?.name}`}
      subtitle={`Choose a store to connect this ${integrationLabel} integration`}
      size="lg"
      height="h-[80vh]"
    >
      <div className="space-y-4">
        {/* Search Filter */}
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            {/* Search Input */}
            <div className="w-full min-w-0 flex-1 sm:w-auto">
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Icon name="Search" className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search businesses by name..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                {searchInput && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-gray-600 transition-colors hover:bg-gray-500"
                    type="button"
                  >
                    <svg
                      className="h-3 w-3 text-gray-300 hover:text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Businesses Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 gap-3 sm:gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-lg border border-gray-800 bg-gray-900 p-4"
              >
                <div className="flex min-w-0 items-start gap-3">
                  {/* Business Logo Skeleton */}
                  <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-gray-800" />

                  <div className="min-w-0 flex-1">
                    {/* Business Name and Connect Button Skeleton */}
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <div className="h-4 w-32 rounded bg-gray-800" />
                      <div className="h-3 w-16 rounded bg-gray-800" />
                    </div>

                    {/* POS Integrations Section Skeleton */}
                    <div className="mt-2 border-t border-gray-700 pt-2">
                      <div className="mb-2 h-3 w-24 rounded bg-gray-800" />
                      <div className="space-y-1.5">
                        {Array.from({ length: 2 }).map((_, j) => (
                          <div
                            key={j}
                            className="flex items-center justify-between gap-2"
                          >
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <div className="h-5 w-5 flex-shrink-0 rounded bg-gray-800" />
                              <div className="h-3 w-20 rounded bg-gray-800" />
                            </div>
                            <div className="h-4 w-16 flex-shrink-0 rounded bg-gray-800" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions Section Skeleton */}
                    <div className="mt-3 border-t border-gray-700 pt-3">
                      <div className="mb-2 h-3 w-28 rounded bg-gray-800" />
                      <div className="flex gap-2">
                        <div className="h-8 flex-1 rounded bg-gray-800" />
                        <div className="h-8 flex-1 rounded bg-gray-800" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-500/30 bg-red-900/20 p-6">
            <div className="flex items-center space-x-3">
              <div className="text-red-500">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-red-400">
                  Failed to load businesses
                </h3>
                <p className="mt-1 text-sm text-red-300">
                  {error.message ||
                    "Something went wrong while loading businesses. Please try again."}
                </p>
                <button
                  onClick={handleRefresh}
                  className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        ) : businesses.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              {businesses.map((business) => (
                <BusinessCard
                  key={business.id}
                  business={business}
                  integration={integration}
                  onSelect={handleBusinessSelect}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination?.total_pages > 1 && (
              <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
                <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                  <div className="text-sm text-gray-400">
                    {(() => {
                      const total = pagination?.total || 0;
                      const from = pagination?.from || 0;
                      const to = pagination?.to || 0;
                      return (
                        <>
                          Showing{" "}
                          <span className="font-medium text-white">{from}</span>{" "}
                          to{" "}
                          <span className="font-medium text-white">{to}</span>{" "}
                          of{" "}
                          <span className="font-medium text-white">
                            {total}
                          </span>{" "}
                          businesses
                        </>
                      );
                    })()}
                  </div>

                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.total_pages}
                    onPageChange={handlePageChange}
                    maxVisiblePages={5}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex min-h-[400px] flex-col items-center justify-center py-16 text-center">
            <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gray-800">
              <Icon name="Store" className="h-12 w-12 text-gray-500" />
            </div>
            <h3 className="mb-2 text-lg font-medium text-white">
              {searchTerm ? "No businesses found" : "No businesses available"}
            </h3>
            <p className="mx-auto max-w-md text-sm text-gray-400">
              {searchTerm ? (
                <>
                  No businesses match your search &quot;
                  <span className="font-medium text-gray-300">
                    {searchTerm}
                  </span>
                  &quot;. Try adjusting your search terms or{" "}
                  <button
                    onClick={handleClearSearch}
                    className="text-blue-500 underline transition-colors hover:text-blue-400"
                  >
                    clear the search
                  </button>{" "}
                  to see all businesses.
                </>
              ) : (
                "There are no businesses available at the moment. Please create a business first to connect this integration."
              )}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BusinessSelectionModal;
